import { agentIdentity, type AgentIdentity } from "../agent.ts";
import { attrs, cx, html, type Html, type Part } from "../html.ts";
import { STATUS, type RunStatus } from "../status.ts";

export interface StatusPillProps {
  status: RunStatus;
  /** Override the word. Keep it a fixed-vocabulary word unless the state is not a run status. */
  label?: string | undefined;
  size?: "sm" | "md";
  /** The pulsing live dot. The only idle animation in the app. */
  live?: boolean;
}

/** bt-pill: glyph (from CSS) plus word, always. */
export function statusPill(props: StatusPillProps): Html {
  const s = STATUS[props.status];
  return html`<span class="${cx("bt-pill", s.pillClass, props.size === "sm" && "bt-pill--sm", props.live && "bt-pill--live")}">${props.label ?? s.label}</span>`;
}

/** The glyph on its own, for timelines and check rows. Always sits next to a word. */
export function statusGlyph(status: RunStatus): Html {
  return html`<span class="bt-glyph ${STATUS[status].glyphClass}" aria-hidden="true"></span>`;
}

export interface AgentBadgeProps {
  /** Join-order index (1-based), or a resolved identity. */
  agent: number | AgentIdentity;
  size?: "xs" | "sm" | "md" | "lg";
  state?: "working" | "idle" | undefined;
}

const identityOf = (agent: number | AgentIdentity): AgentIdentity => (typeof agent === "number" ? agentIdentity(agent) : agent);

/** bt-agent: colour fill plus head shape. The callsign is always the accessible name. */
export function agentBadge(props: AgentBadgeProps): Html {
  const id = identityOf(props.agent);
  return html`<span${attrs({
    class: cx("bt-agent", props.size && props.size !== "sm" && `bt-agent--${props.size}`, id.badgeClass, id.headClass),
    "data-state": props.state,
    role: "img",
    "aria-label": id.callsign,
  })}></span>`;
}

export interface UnitProps extends AgentBadgeProps {
  /** A friendly name beside the callsign, e.g. "Claude Code". */
  name?: string;
}

/** bt-unit: badge, callsign in mono, optional name. */
export function unit(props: UnitProps): Html {
  const id = identityOf(props.agent);
  const text = props.name
    ? html`<span class="bt-unit__text"><span class="bt-unit__call">${id.callsign}</span><span class="bt-unit__name">${props.name}</span></span>`
    : html`<span class="bt-unit__call">${id.callsign}</span>`;
  return html`<span class="bt-unit">${agentBadge({ ...props, agent: id })}${text}</span>`;
}

export interface TimelineItemProps {
  status: RunStatus;
  /** The sentence. Wrap callsigns in <b> via html``. */
  title: Part;
  meta?: Part;
  time?: string;
  /** The item the scrubber or J/K selection is on. */
  active?: boolean;
  /** Show this agent's badge in the node instead of the status glyph (the status class still colours the rail). */
  agent?: number;
  /** Extra attributes, e.g. data-event ids for J/K selection. */
  attrs?: Readonly<Record<string, string | number | boolean | null | undefined>>;
}

/** bt-tl-item. Put several inside `timeline`. */
export function timelineItem(props: TimelineItemProps): Html {
  const s = STATUS[props.status];
  return html`<li${attrs({ class: cx("bt-tl-item", s.timelineClass, props.active && "is-active"), "aria-current": props.active ? "step" : undefined, ...props.attrs })}><span class="bt-tl-item__node">${props.agent !== undefined ? agentBadge({ agent: props.agent }) : statusGlyph(props.status)}</span><div class="bt-tl-item__body"><div class="bt-tl-item__title">${props.title}</div>${props.meta !== undefined ? html`<div class="bt-tl-item__meta">${props.meta}</div>` : ""}</div>${props.time ? html`<span class="bt-tl-item__time">${props.time}</span>` : ""}</li>`;
}

export function timeline(items: readonly Html[], ariaLabel?: string): Html {
  return html`<ol${attrs({ class: "bt-timeline", "aria-label": ariaLabel })}>${items}</ol>`;
}
